// export const BASE_URL = "http://192.168.2.6:8080";

// export async function apiRequest(endpoint, method = "GET", body = null) {
//   try {
//     const response = await fetch(`${BASE_URL}${endpoint}`, {
//       method,
//       headers: { "Content-Type": "application/json" },
//       body: body ? JSON.stringify(body) : null,
//     });

//     if (!response.ok) {
//       const msg = await response.text();
//       throw new Error(msg || "API Error");
//     }

//     return response.json();
//   } catch (error) {
//     console.error("API Error:", error);
//     throw error;
//   }
// }

// src/services/axiosInstance.js
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import {
  refreshInProgress as timerRefreshInProgress,
  refreshToken as timerRefreshToken,
} from "./tokenUtils";
import { APP_VERSION, APP_CHANNEL } from "../version";

export const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:1010/api";

const API = axios.create({
  baseURL: API_BASE,
  timeout: 60000,
  headers: { "Content-Type": "application/json" },
});

// Queue to store failed requests during token refresh
let isRefreshing = false;
let failedQueue = [];
let lastInterceptorRefresh = 0;

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Helper function to check if token needs refresh
const shouldRefreshToken = (token) => {
  try {
    if (!token) return false;

    const decoded = jwtDecode(token);
    if (!decoded?.exp || !decoded?.iat) return false;

    const now = Date.now() / 1000;
    const timeRemaining = decoded.exp - now;
    const totalLifetime = decoded.exp - decoded.iat;

    // Refresh if less than 20% of lifetime remains (matching tokenUtils.js)
    const refreshThreshold = totalLifetime * 0.2;

    // Only refresh if more than 5 seconds remaining
    return timeRemaining > 5 && timeRemaining <= refreshThreshold;
  } catch (error) {
    console.error("Error checking token refresh:", error);
    return false;
  }
};

// Helper function to refresh token - uses shared refresh from tokenUtils
const refreshAccessToken = async () => {
  // Check if tokenUtils timer is already refreshing
  if (timerRefreshInProgress) {
    console.log("🔄 Timer already refreshing, waiting...");
    // Wait a bit and return current token
    await new Promise((resolve) => setTimeout(resolve, 1000));
    const token = localStorage.getItem("token");
    if (token) return token;
    throw new Error("No token after waiting for timer refresh");
  }

  // Prevent rapid refresh attempts (minimum 5 seconds)
  const now = Date.now();
  if (now - lastInterceptorRefresh < 5000) {
    console.log("⏳ Too soon since last interceptor refresh");
    const token = localStorage.getItem("token");
    if (token) return token;
    throw new Error("Too soon to refresh");
  }

  try {
    lastInterceptorRefresh = now;

    // Use the shared refresh function from tokenUtils
    const refreshed = await timerRefreshToken("interceptor");

    if (refreshed) {
      const newToken = localStorage.getItem("token");
      console.log("✅ Token refreshed via interceptor");
      return newToken;
    }

    throw new Error("Refresh failed");
  } catch (error) {
    console.error("Token refresh failed:", error);
    throw error;
  }
};

// 👉 Request Interceptor - Attach token and auto-refresh if needed
API.interceptors.request.use(
  async (config) => {
    // Report the running build + channel on every request so the server's
    // version gate can reject clients below the configured minimum. Applies to
    // unauthenticated calls too (/users/key, /users/login), which is where the
    // login gate actually reads it.
    config.headers["X-App-Version"] = APP_VERSION;
    config.headers["X-App-Channel"] = APP_CHANNEL;

    const token = localStorage.getItem("token");

    if (token) {
      // Check if token needs refresh before making request
      // Skip if timer or interceptor is already refreshing
      if (
        shouldRefreshToken(token) &&
        !isRefreshing &&
        !timerRefreshInProgress
      ) {
        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();
          config.headers.Authorization = newToken;
          processQueue(null, newToken);
        } catch (error) {
          processQueue(error, null);
          // Don't throw - let request proceed with current token
          config.headers.Authorization = localStorage.getItem("token") || token;
        } finally {
          isRefreshing = false;
        }
      } else {
        // Use latest token from storage (may have been refreshed by timer)
        config.headers.Authorization = localStorage.getItem("token") || token;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// 👉 Response Interceptor - Handle 401 errors and retry
API.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Skip retry for refresh-token endpoint to avoid infinite loop
    if (originalRequest?.url?.includes("refresh-token")) {
      return Promise.reject(error);
    }

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // If timer or interceptor is already refreshing, queue this request
      if (isRefreshing || timerRefreshInProgress) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = token;
            return API(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = newToken;
        processQueue(null, newToken);
        return API(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // Token refresh failed - check if token is really expired before logout
        const token = localStorage.getItem("token");
        if (token) {
          try {
            const decoded = jwtDecode(token);
            if (decoded?.exp && decoded.exp * 1000 > Date.now()) {
              // Token not expired yet, don't logout
              console.log(
                "⚠️ Refresh failed but token not expired, continuing...",
              );
              return Promise.reject(refreshError);
            }
          } catch (e) {
            // Token decode failed
          }
        }

        // Token is expired or invalid - logout user
        console.log("🚪 Token expired or invalid - logging out");
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("loggedUser");
        localStorage.setItem("logout", Date.now().toString());
        window.location.replace("/login");

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default API;
